/**
 * MobileEarlyAccessStats — a clickable, real-time Early Access counter for the
 * TOP-LEFT of the mobile header. Mirrors the desktop "残り枠数 120,000" strip
 * (which is hidden on mobile) and adds a tap-to-open popup so visitors can
 * follow live signups per track. Polls the public /api/early-access/counts.
 */
import { useState, useEffect, useRef } from 'react';
import { Rocket, X, Users, Building2, Landmark, ArrowRight, CalendarClock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useLaunchCountdown, LAUNCH_LABEL } from '@/lib/useLaunchCountdown';

const API = process.env.REACT_APP_BACKEND_URL;

const TRACKS = [
  { key: 'personal', Icon: Users, color: '#9200C7', en: 'Personal', ja: '個人' },
  { key: 'business', Icon: Building2, color: '#0083D3', en: 'Business', ja: '法人' },
  { key: 'government', Icon: Landmark, color: '#0066CC', en: 'Government', ja: '官公庁' },
];

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

export default function MobileEarlyAccessStats({ prefix = '' }) {
  const { lang } = useLanguage();
  const ja = lang === 'ja';
  const [counts, setCounts] = useState(null);
  const [open, setOpen] = useState(false);
  // CSS-only enter/exit in place of framer-motion's AnimatePresence: the popup
  // stays mounted a bit past `open=false` so the closing transition can play,
  // then unmounts (avoids keeping an always-hidden node in the layout).
  const [popupPhase, setPopupPhase] = useState('closed'); // closed | entering | open | closing
  const ref = useRef(null);
  const { days, hours } = useLaunchCountdown(60000);
  const reduceMotion = usePrefersReducedMotion();

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${API}/api/early-access/counts`);
        if (r.ok) { const d = await r.json(); if (!cancelled) setCounts(d); }
      } catch { /* non-fatal */ }
    };
    load();
    const t = setInterval(load, 25000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  useEffect(() => {
    if (open) {
      setPopupPhase('entering');
      const raf = requestAnimationFrame(() => setPopupPhase('open'));
      return () => cancelAnimationFrame(raf);
    }
    if (popupPhase === 'closed') return undefined;
    setPopupPhase('closing');
    const t = setTimeout(() => setPopupPhase('closed'), reduceMotion ? 0 : 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reserved = counts?.total ? counts.total.confirmed : null;
  const remaining = counts?.total ? counts.total.remaining : null;
  const popupVisible = popupPhase !== 'closed';
  const popupIn = popupPhase === 'open';

  return (
    <div className="lg:hidden relative flex-shrink-0" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="group inline-flex items-center gap-1.5 rounded-full pl-2 pr-2.5 py-1.5 bg-gradient-to-r from-[#9200C7]/10 to-[#0083D3]/10 ring-1 ring-[#9200C7]/25 active:scale-95 transition-transform"
        aria-label={ja ? '先行受付の状況' : 'Early Access live stats'}
        data-testid="mobile-ea-stats-trigger"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70 animate-ping" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>
        <Rocket className="w-3.5 h-3.5 text-[#9200C7]" strokeWidth={2.4} />
        <span className="text-[12px] font-extrabold tabular-nums text-slate-800" data-testid="mobile-ea-stats-count">
          {reserved == null ? '··' : fmt(reserved)}
        </span>
      </button>

      {popupVisible && (
        <div
          style={{ transitionDuration: reduceMotion ? '0ms' : '220ms' }}
          className={`absolute left-0 top-full mt-2 w-[270px] rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden z-50 transition-[opacity,transform] ease-out ${
            popupIn ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-2 scale-[0.97]'
          }`}
          data-testid="mobile-ea-stats-popup"
        >
            <div className="px-4 py-3 bg-gradient-to-r from-[#9200C7] to-[#0083D3] text-white">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider">
                  <span className="relative flex h-1.5 w-1.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white opacity-70 animate-ping" />
                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                  </span>
                  {ja ? '先行受付・ライブ' : 'Early Access · Live'}
                </span>
                <button onClick={() => setOpen(false)} className="p-0.5 -mr-1 rounded hover:bg-white/15" data-testid="mobile-ea-stats-close">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-1.5 flex items-end gap-1.5">
                <span className="text-3xl font-black leading-none tabular-nums" data-testid="mobile-ea-reserved-total">{fmt(reserved)}</span>
                <span className="text-[12px] font-semibold text-white/85 mb-0.5">{ja ? '名が予約済み' : 'reserved'}</span>
              </div>
              <p className="text-[11px] text-white/80 mt-0.5">
                {ja ? `残り枠数 ${fmt(remaining)}` : `${fmt(remaining)} spots remaining`}
              </p>
            </div>

            <div className="p-2.5">
              <div className="px-1 pb-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                {ja ? '対象のアカウントタイプ' : 'Eligible account types'}
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                {TRACKS.map((t) => (
                  <Link
                    key={t.key}
                    to={`${prefix}/${t.key}`}
                    onClick={() => setOpen(false)}
                    className="rounded-xl px-2 py-2.5 bg-slate-50 hover:bg-slate-100 flex flex-col items-center gap-1 transition-colors"
                    data-testid={`mobile-ea-track-${t.key}`}
                  >
                    <t.Icon className="w-4 h-4" style={{ color: t.color }} strokeWidth={2.4} />
                    <span className="text-[10px] font-bold text-slate-600">{ja ? t.ja : t.en}</span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Launch countdown — drives urgency to reserve now */}
            <div className="mx-2.5 mb-2 rounded-xl bg-slate-900 px-3 py-2.5 flex items-center justify-between" data-testid="mobile-ea-launch-countdown">
              <div className="min-w-0">
                <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-white/45">
                  <CalendarClock className="w-3 h-3" />{ja ? '公開予定日' : 'Estimated launch'}
                </div>
                <div className="text-[12px] font-bold text-white mt-0.5">{ja ? LAUNCH_LABEL.ja : LAUNCH_LABEL.en}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-2xl font-black leading-none tabular-nums text-emerald-400">{days}</div>
                <div className="text-[9px] text-white/50 mt-0.5">{ja ? `日 ${hours}時間` : `days · ${hours}h`}</div>
              </div>
            </div>

            <Link to={`${prefix}/early-access`} onClick={() => setOpen(false)}
              className="flex items-center justify-center gap-1.5 mx-2.5 mb-2.5 rounded-xl bg-[#9200C7] px-3 py-2.5 text-[13px] font-extrabold text-white hover:bg-[#7b00a8] active:scale-[0.98] transition-all"
              data-testid="mobile-ea-stats-cta">
              {ja ? '枠を予約する' : 'Reserve your spot'} <ArrowRight className="w-4 h-4" />
            </Link>
        </div>
      )}
    </div>
  );
}
